import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Smartphone, 
  Monitor, 
  Download, 
  Share, 
  Plus, 
  MoreVertical,
  Check,
  Wifi,
  Bell,
  Zap
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { installTranslations } from "@/i18n/installTranslations";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const InstallPage = () => {
  const { language } = useLanguage();
  const t = installTranslations[language];
  
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deviceType, setDeviceType] = useState<"ios" | "android" | "desktop">("desktop");

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType("ios");
    } else if (/android/.test(userAgent)) {
      setDeviceType("android");
    } else {
      setDeviceType("desktop");
    }

    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const features = [
    { icon: Zap, title: t.features.fast.title, description: t.features.fast.description },
    { icon: Wifi, title: t.features.offline.title, description: t.features.offline.description },
    { icon: Bell, title: t.features.notifications.title, description: t.features.notifications.description },
  ];

  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
      </Helmet>

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Smartphone className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t.hero.title}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              {t.hero.subtitle}
            </p>

            {isInstalled ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full">
                <Check className="w-5 h-5" />
                <span className="font-medium">{t.hero.installedMessage}</span>
              </div>
            ) : deferredPrompt ? (
              <Button size="lg" onClick={handleInstallClick} className="gap-2">
                <Download className="w-5 h-5" />
                {t.hero.installButton}
              </Button>
            ) : (
              <p className="text-muted-foreground">
                {t.hero.followInstructions}
              </p>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="text-center border-0 shadow-sm">
                  <CardContent className="pt-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Device-Specific Instructions */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-12">
              {t.instructions.title}
            </h2>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* iOS Instructions */}
              <Card className={`${deviceType === "ios" ? "ring-2 ring-primary" : ""}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{t.instructions.ios.title}</CardTitle>
                      <CardDescription>{t.instructions.ios.subtitle}</CardDescription>
                    </div>
                  </div>
                  {deviceType === "ios" && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full w-fit">
                      {t.instructions.yourDevice}
                    </span>
                  )}
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {t.instructions.ios.steps.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-foreground font-medium flex items-center gap-2">
                            {step.title}
                            {index === 1 && <Share className="w-4 h-4" />}
                            {index === 2 && <Plus className="w-4 h-4" />}
                          </p>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Android Instructions */}
              <Card className={`${deviceType === "android" ? "ring-2 ring-primary" : ""}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{t.instructions.android.title}</CardTitle>
                      <CardDescription>{t.instructions.android.subtitle}</CardDescription>
                    </div>
                  </div>
                  {deviceType === "android" && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full w-fit">
                      {t.instructions.yourDevice}
                    </span>
                  )}
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {t.instructions.android.steps.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-foreground font-medium flex items-center gap-2">
                            {step.title}
                            {index === 1 && <MoreVertical className="w-4 h-4" />}
                            {index === 2 && <Download className="w-4 h-4" />}
                          </p>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Desktop Instructions */}
              <Card className={`${deviceType === "desktop" ? "ring-2 ring-primary" : ""}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                      <Monitor className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{t.instructions.desktop.title}</CardTitle>
                      <CardDescription>{t.instructions.desktop.subtitle}</CardDescription>
                    </div>
                  </div>
                  {deviceType === "desktop" && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full w-fit">
                      {t.instructions.yourDevice}
                    </span>
                  )}
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {t.instructions.desktop.steps.map((step, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-foreground font-medium flex items-center gap-2">
                            {step.title}
                            {index === 1 && <Download className="w-4 h-4" />}
                          </p>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {t.cta.title}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t.cta.subtitle}
            </p>
            <Button asChild size="lg">
              <a href="/triage">{t.cta.button}</a>
            </Button>
          </div>
        </section>
      </main>
    </>
  );
};

export default InstallPage;
